import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Button,
    Box,
    Container,
    Typography,
    Menu,
    MenuItem,
    Fade,
} from '@mui/material';
import { KeyboardArrowRight } from '@mui/icons-material';
import logo from "../../Assests/logo.svg"
import { useNavigate } from 'react-router-dom';

const NavBar = ({ textColor = '#ffffff', logoTextColor = '#ffffff' }) => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeMenu, setActiveMenu] = useState(null);
    const [activeNavItem, setActiveNavItem] = useState('Products');

    const handleClick = (event, menuType) => {
        setAnchorEl(event.currentTarget);
        setActiveMenu(menuType);
        setActiveNavItem(menuType);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setActiveMenu(null);
    };

    const handleNavigation = (item) => {
        setActiveNavItem(item.label);
        if (!item.hasDropdown) {
            navigate(item.href);
        }
    };

    const handleDropdownItemClick = (href) => {
        navigate(href);
        handleClose();
    };

    const handleLogoClick = () => {
        navigate('/');
        setActiveNavItem('Home');
    };

    const navigationItems = [
        {
            label: 'Products',
            href: '/products',
            hasDropdown: true,
            isTwoColumn: true,
            leftColumnItems: [
                { label: 'Payment Gateways', desc: 'Designed for speed, built for trust.', href: '/payment-gateways' },
                { label: 'Payment Integration', desc: 'Fast integration, Frictionless payments.', href: '/payment-integration' },
                { label: 'Payouts', desc: 'Move money effortlessly with Zutopay.', href: '/payouts' },
                { label: 'QR Code Solutions', desc: 'Scan, Pay, Done.', href: '/qr-solutions' },
                { label: 'Domestic Payments', desc: 'Local level payments made simple.', href: '/domestic-payments' },
                { label: 'PoS Solutions', desc: 'We offers a wide range of products, including (POS) terminals', href: '/pos-solutions' }
            ],
            rightColumnItems: [
                { label: 'Hosted Payment Gateways', desc: 'Easy to set up, low risk', href: '/hosted-gateways' },
                { label: 'Self-Hosted Payment Gateways', desc: 'Full branding control', href: '/self-hosted-gateways' },
                { label: 'Local Bank Integration Gateways', desc: 'Familiar for users, simple to integrate', href: '/local-bank-gateways' },
                { label: 'Mobile Payment Gateways', desc: 'Fast and user-friendly', href: '/mobile-gateways' }
            ]
        },
        {
            label: 'Developers',
            href: '/developers',
            hasDropdown: true,
            isTwoColumn: false,
            dropdownItems: [
                { label: 'Developer Documentation', desc: 'Start your integration', href: '/company/developers', isHighlighted: true },
                { label: 'API References', desc: "Explore our API's", href: '/company/developers/api-references' }
            ]
        },
        {
            label: 'Industry',
            href: '/industry',
            hasDropdown: true,
            isTwoColumn: false,
            dropdownItems: [
                { label: 'Travel', desc: 'Travel light. Pay smart.', href: '/travel', isHighlighted: true },
                { label: 'Insurance', desc: 'One app for all your coverage payments.', href: '/insurance' },
                { label: 'Edutech', desc: 'Smart payments, smarter learning.', href: '/edutech' },
                { label: 'E-Commerce', desc: 'E-commerce payments made effortless.', href: '/ecommerce' },
                { label: 'Wealth', desc: 'Payments that move with your money.', href: '/wealth' },
                { label: 'Corporate', desc: 'Fast. Secure. Scalable. Built for corporates.', href: '/corporate' }
            ]
        },
        {
            label: 'Pricing',
            href: '/pricing',
            hasDropdown: false
        },
        {
            label: 'Company',
            href: '/company',
            hasDropdown: true,
            isTwoColumn: false,
            dropdownItems: [
                { label: 'About Us', desc: 'Zutopay is here to make payments easy for everyone.', href: '/about-us', isHighlighted: true },
                { label: 'Board of Directors', desc: 'Building trust and growth through expert guidance.', href: '/board' },
                { label: 'Blogs & News', desc: 'Insights, Innovations & Announcements', href: '/blogs' },
                { label: 'Careers', desc: 'Work for Zutopay, Grow your knowledge', href: '/careers' },
                { label: 'Contact Us', desc: 'Get in Touch with Zutopay', href: '/contact' }
            ]
        },
    ];

    return (
        <AppBar
            sx={{
                position: "absolute",
                top: "30px",
                left: 0,
                right: 0,
                background: 'transparent',
                backdropFilter: 'none',
                boxShadow: 'none',
                zIndex: 1100,
                px: 4
            }}
        >
            <Container maxWidth="xl">
                <Toolbar
                    sx={{
                        display: "flex",
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {/* Logo Section - Left Side with Home Navigation */}
                    <Box
                        onClick={handleLogoClick}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1, // Reduced gap
                            flexGrow: 0,
                            cursor: 'pointer',
                        }}
                    >
                        <Box
                            component="img"
                            src={logo}
                            alt="ZutoPay Logo"
                            sx={{
                                width: "38px", // Slightly larger logo
                                height: "auto",
                                display: 'block',
                            }}
                        />
                        <Typography
                            sx={{
                                color: logoTextColor,
                                fontSize: '2.5rem', // Adjusted size
                                fontWeight: 500,
                                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                                letterSpacing: '-1px',
                                fontStyle: 'italic',
                                textTransform: 'lowercase',
                                lineHeight: 1,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            zutopay
                        </Typography>
                    </Box>

                    {/* Navigation Links - Right */}
                    <Box
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            gap: 1,
                            alignItems: 'center',
                        }}
                    >
                        {navigationItems.map((item, index) => (
                            <Box key={item.label} sx={{ position: 'relative' }}>
                                <Typography
                                    onClick={(e) => {
                                        if (item.hasDropdown) {
                                            handleClick(e, item.label);
                                        } else {
                                            handleNavigation(item);
                                        }
                                    }}
                                    component="span"
                                    sx={{
                                        color: activeNavItem === item.label ? '#00d4aa' : textColor,
                                        fontSize: '1rem',
                                        fontWeight: 400,
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        textDecoration: 'none',
                                    }}
                                >
                                    {item.label}
                                </Typography>

                                {item.hasDropdown && (
                                    <Menu
                                        anchorEl={anchorEl}
                                        open={activeMenu === item.label}
                                        onClose={handleClose}
                                        TransitionComponent={Fade}
                                        sx={{
                                            '& .MuiPaper-root': {
                                                backgroundColor: '#ffffff',
                                                borderRadius: '16px',
                                                boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)',
                                                minWidth: item.isTwoColumn ? '800px' : '350px',
                                                mt: 1.5,
                                                border: '1px solid rgba(0, 0, 0, 0.08)',
                                                overflow: 'hidden',
                                            },
                                        }}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'center',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'center',
                                        }}
                                    >
                                        {item.isTwoColumn ? (
                                            <Box sx={{ display: 'flex', minHeight: '420px' }}>
                                                <Box sx={{
                                                    flex: 1,
                                                    borderRight: '1px solid rgba(0, 0, 0, 0.08)',
                                                    py: 2
                                                }}>
                                                    {item.leftColumnItems.map((leftItem, leftIndex) => (
                                                        <MenuItem
                                                            key={leftItem.label}
                                                            onClick={() => handleDropdownItemClick(leftItem.href)}
                                                            sx={{
                                                                flexDirection: 'column',
                                                                alignItems: 'flex-start',
                                                                padding: '18px 28px',
                                                                borderBottom: leftIndex < item.leftColumnItems.length - 1 ?
                                                                    '1px solid rgba(0, 0, 0, 0.06)' : 'none',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(0, 212, 170, 0.04)',
                                                                },
                                                                '&:last-child': {
                                                                    borderBottom: 'none',
                                                                },
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                width: '100%',
                                                                mb: 1
                                                            }}>
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        color: leftIndex === 0 ? '#00d4aa' : '#1a1a1a',
                                                                        fontSize: '1rem',
                                                                    }}
                                                                >
                                                                    {leftItem.label}
                                                                </Typography>
                                                                <KeyboardArrowRight
                                                                    sx={{
                                                                        fontSize: '20px',
                                                                        color: '#999',
                                                                        ml: 2
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Typography
                                                                sx={{
                                                                    color: '#888',
                                                                    fontSize: '0.9rem',
                                                                    lineHeight: 1.5,
                                                                }}
                                                            >
                                                                {leftItem.desc}
                                                            </Typography>
                                                        </MenuItem>
                                                    ))}
                                                </Box>

                                                <Box sx={{ flex: 1, py: 2 }}>
                                                    {item.rightColumnItems.map((rightItem, rightIndex) => (
                                                        <MenuItem
                                                            key={rightItem.label}
                                                            onClick={() => handleDropdownItemClick(rightItem.href)}
                                                            sx={{
                                                                flexDirection: 'column',
                                                                alignItems: 'flex-start',
                                                                padding: '18px 28px',
                                                                borderBottom: rightIndex < item.rightColumnItems.length - 1 ?
                                                                    '1px solid rgba(0, 0, 0, 0.06)' : 'none',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(0, 212, 170, 0.04)',
                                                                },
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: '#1a1a1a',
                                                                    fontSize: '1rem',
                                                                    mb: 1,
                                                                }}
                                                            >
                                                                {rightItem.label}
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    color: '#888',
                                                                    fontSize: '0.9rem',
                                                                    lineHeight: 1.5,
                                                                }}
                                                            >
                                                                {rightItem.desc}
                                                            </Typography>
                                                        </MenuItem>
                                                    ))}
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Box sx={{ py: 2 }}>
                                                {item.dropdownItems.map((dropdownItem, dropdownIndex) => (
                                                    <MenuItem
                                                        key={dropdownItem.label}
                                                        onClick={() => handleDropdownItemClick(dropdownItem.href)}
                                                        sx={{
                                                            flexDirection: 'column',
                                                            alignItems: 'flex-start',
                                                            padding: '18px 28px',
                                                            borderBottom: dropdownIndex < item.dropdownItems.length - 1 ?
                                                                '1px solid rgba(0, 0, 0, 0.06)' : 'none',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(0, 212, 170, 0.04)',
                                                            },
                                                            position: 'relative',
                                                            borderLeft: dropdownItem.isHighlighted ? '4px solid #00d4aa' : 'none',
                                                            backgroundColor: dropdownItem.isHighlighted ?
                                                                'rgba(0, 212, 170, 0.02)' : 'transparent',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: dropdownItem.isHighlighted ? '#00d4aa' : '#1a1a1a',
                                                                fontSize: '1rem',
                                                                mb: 1,
                                                            }}
                                                        >
                                                            {dropdownItem.label}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                color: '#888',
                                                                fontSize: '0.9rem',
                                                                lineHeight: 1.5,
                                                            }}
                                                        >
                                                            {dropdownItem.desc}
                                                        </Typography>
                                                    </MenuItem>
                                                ))}
                                            </Box>
                                        )}
                                    </Menu>
                                )}
                            </Box>
                        ))}
                        <Button
                            variant="contained"
                            onClick={() => navigate('/signup')}
                            sx={{
                                background: 'linear-gradient(135deg, #00d4aa 0%, #00b894 100%)',
                                color: '#ffffff',
                                textTransform: 'capitalize',
                                fontWeight: 400,
                                padding: '10px 20px',
                                borderRadius: '10px',
                                minWidth: '120px',
                                ml: 2,
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #00b894 0%, #00a085 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(0, 212, 170, 0.3)',
                                },
                                transition: 'all 0.3s ease',
                            }}
                        >
                            Sign Up
                        </Button>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
};

export default NavBar;